import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Wrapper from "../../../components/Wrapper";
import { labApi, hardwareApi } from "../../../services/labApi";
import { selectCurrentRole, selectLabRole, selectRoleLoading } from "../../../store/features/roleSlice";
import { canManageLabCrewRoster, isLabCrewPortalOnly, normalizeLabRole } from "../../../utils/role";

export default function CrewPage() {
  const loginRole = useSelector(selectCurrentRole);
  const labRole = useSelector(selectLabRole);
  const roleLoading = useSelector(selectRoleLoading);
  const manageRoster = canManageLabCrewRoster({ loginRole, labRole });
  const portalOnly = isLabCrewPortalOnly({ loginRole, labRole });

  const [crew, setCrew] = useState([]);
  const [availableAdmins, setAvailableAdmins] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [portal, setPortal] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [addForm, setAddForm] = useState({ super_admin_id: "", role: "Lab Tech" });
  const [workForm, setWorkForm] = useState({ reagent_id: "", hardware_id: "", quantity_completed: "", start_time: "", end_time: "", notes: "" });
  const [reagents, setReagents] = useState([]);
  const [hardwareList, setHardwareList] = useState([]);
  const [portalError, setPortalError] = useState("");
  const [rosterError, setRosterError] = useState("");

  const attendance = portal?.attendance || null;
  const targets = Array.isArray(portal?.targets) ? portal.targets : [];
  const workLogs = Array.isArray(portal?.work_logs) ? portal.work_logs : [];
  const totalCompleted = workLogs.reduce(
    (sum, log) => sum + Number(log.quantity_produced || log.quantity_completed || 0),
    0
  );
  const totalTarget = targets.reduce(
    (sum, target) => sum + Number(target.target_quantity || 0),
    0
  );
  const progressPercent = totalTarget > 0 ? Math.round((totalCompleted / totalTarget) * 100) : 0;

  const loadRoster = async () => {
    const [crewRows, adminsRows, reagentRows, hardwareRows] = await Promise.all([
      labApi.getCrew(),
      labApi.getAvailableAdmins(),
      labApi.getReagents(),
      hardwareApi.getHardware(),
    ]);
    setCrew(crewRows);
    setAvailableAdmins(adminsRows);
    setReagents(reagentRows);
    setHardwareList(hardwareRows);
  };

  const loadPortalOnly = async () => {
    const [reagentRows, hardwareRows] = await Promise.all([
      labApi.getReagents(),
      hardwareApi.getHardware()
    ]);
    setReagents(reagentRows);
    setHardwareList(hardwareRows);
  };

  const load = useCallback(async () => {
    setRosterError("");
    if (manageRoster) {
      await loadRoster();
    } else {
      await loadPortalOnly();
    }
  }, [manageRoster]);

  const loadPortal = useCallback(async () => {
    try {
      setPortalError("");
      const data = await labApi.getMyPortal(date);
      setPortal(data);
    } catch (error) {
      if (error?.response?.status === 403) {
        setPortal(null);
        setPortalError("You are not an active lab crew member, so Crew Portal data is not available.");
        return;
      }
      throw error;
    }
  }, [date]);

  useEffect(() => {
    if (roleLoading) return;
    load().catch(console.error);
  }, [load, roleLoading]);

  useEffect(() => {
    loadPortal().catch(console.error);
  }, [loadPortal]);

  const addMember = async (e) => {
    e.preventDefault();
    if (!manageRoster) return;
    try {
      setRosterError("");
      await labApi.addCrewMember(addForm);
      setAddForm({ super_admin_id: "", role: "Lab Tech" });
      await load();
    } catch (err) {
      setRosterError(err?.response?.data?.message || "Could not add crew member.");
    }
  };

  const toggleMember = async (member) => {
    if (!manageRoster) return;
    try {
      setRosterError("");
      await labApi.updateCrewMember(member.id, { is_active: !member.is_active });
      await load();
    } catch (err) {
      setRosterError(err?.response?.data?.message || "Could not update member.");
    }
  };

  const removeMember = async (id) => {
    if (!manageRoster) return;
    try {
      setRosterError("");
      await labApi.deleteCrewMember(id);
      await load();
    } catch (err) {
      setRosterError(err?.response?.data?.message || "Could not remove member.");
    }
  };

  const doClockIn = async () => {
    const time = new Date().toTimeString().slice(0, 5);
    await labApi.clockIn({ date, time });
    await loadPortal();
  };

  const doClockOut = async () => {
    const time = new Date().toTimeString().slice(0, 5);
    await labApi.clockOut({ date, time });
    await loadPortal();
  };

  const submitMyWork = async (e) => {
    e.preventDefault();
    await labApi.logMyWork({
      reagent_id: workForm.reagent_id || null,
      hardware_id: workForm.hardware_id || null,
      quantity_completed: Number(workForm.quantity_completed || 0),
      start_time: workForm.start_time,
      end_time: workForm.end_time || null,
      notes: workForm.notes || null,
      date,
    });
    setWorkForm({ reagent_id: "", hardware_id: "", quantity_completed: "", start_time: "", end_time: "", notes: "" });
    await loadPortal();
  };

  const roleLabel = normalizeLabRole(labRole) || "Support (not on crew roster)";

  // Determine value for the combined select dropdown
  const selectedWorkItemValue = workForm.reagent_id 
    ? `reagent_${workForm.reagent_id}` 
    : workForm.hardware_id 
      ? `hardware_${workForm.hardware_id}` 
      : "";

  const handleWorkItemChange = (e) => {
    const val = e.target.value;
    if (val.startsWith("reagent_")) {
      setWorkForm({ ...workForm, reagent_id: val.replace("reagent_", ""), hardware_id: "" });
    } else if (val.startsWith("hardware_")) {
      setWorkForm({ ...workForm, hardware_id: val.replace("hardware_", ""), reagent_id: "" });
    } else {
      setWorkForm({ ...workForm, hardware_id: "", reagent_id: "" });
    }
  };

  return (
    <Wrapper>
      <div className="space-y-6 pb-20 lg:pb-6">
        <div>
          <h2 className="m-0 text-2xl font-bold text-slate-800">Crew</h2>
          <p className="mt-1 text-sm text-slate-500">
            Signed in as <span className="font-medium text-slate-700">{roleLabel}</span>
            {portalOnly ? " — you can use your portal below. Roster changes are limited to Lab Leads." : null}
            {manageRoster && !portalOnly ? " — you can manage the crew roster." : null}
          </p>
        </div>

        {rosterError ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{rosterError}</div>
        ) : null}

        {manageRoster ? (
          <>
            <form onSubmit={addMember} className="grid md:grid-cols-3 gap-2 bg-white border rounded-xl p-4 shadow-sm">
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-700">Available Members</label>
                <select
                  value={addForm.super_admin_id}
                  onChange={(e) => setAddForm({ ...addForm, super_admin_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="">Select available members</option>
                  {availableAdmins.map((a) => (
                    <option key={a.super_admin_id} value={a.super_admin_id}>
                      {a.name} ({a.email})
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-700">Role</label>
                <select
                  value={addForm.role}
                  onChange={(e) => setAddForm({ ...addForm, role: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="Lab Tech">Lab Tech</option>
                  <option value="QA Specialist">QA Specialist</option>
                  <option value="Lead">Lead</option>
                </select>
              </div>
              <div className="flex items-end">
                <button type="submit" className="w-full px-4 py-2 bg-emerald-600 hover:bg-emerald-700 transition-colors text-white font-medium rounded-lg">
                  Add Member
                </button>
              </div>
            </form>

            <div className="space-y-2">
              {crew.map((m) => (
                <div key={m.id} className="bg-white border rounded-xl p-4 flex items-center justify-between shadow-sm">
                  <button type="button" onClick={() => setSelectedMember(m)} className="text-left">
                    <p className="m-0 font-semibold text-slate-800">{m.name}</p>
                    <p className="m-0 text-sm text-slate-500">
                      {m.role} - <span className={m.is_active ? "text-emerald-600 font-medium" : "text-slate-400"}>{m.is_active ? "active" : "inactive"}</span>
                    </p>
                  </button>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => toggleMember(m)}
                      className="px-3 py-1.5 border rounded-lg text-sm hover:bg-slate-50 transition-colors"
                    >
                      {m.is_active ? "Deactivate" : "Activate"}
                    </button>
                    <button
                      type="button"
                      onClick={() => removeMember(m.id)}
                      className="px-3 py-1.5 border border-red-100 rounded-lg text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : null}

        {portalOnly ? (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
            Your lab role does not include roster management. Use the portal below for attendance and work logs.
          </div>
        ) : null}

        <div className="bg-white border rounded-xl p-5 shadow-sm space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <h3 className="text-lg font-semibold m-0 text-slate-800">Crew Portal</h3>
            <div className="flex items-center gap-3">
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="px-3 py-2 border rounded-lg text-sm" />
              <div className="flex gap-2">
                <button type="button" onClick={doClockIn} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors">
                  Clock In
                </button>
                <button type="button" onClick={doClockOut} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors">
                  Clock Out
                </button>
              </div>
            </div>
          </div>

          {portalError ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              {portalError}
            </div>
          ) : null}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-xl border border-slate-200 p-4">
              <h4 className="font-semibold text-slate-800 m-0">Attendance</h4>
              <p className="text-sm text-slate-500 mb-3">Track your work hours</p>
              {!attendance ? (
                <p className="text-sm text-slate-500 m-0 italic">No attendance log for selected date.</p>
              ) : (
                <div className="space-y-1.5 text-sm">
                  <p className="m-0 text-slate-700 flex justify-between border-b pb-1">
                    <span>Checked in:</span>
                    <span className="font-medium text-slate-900">
                      {attendance.clock_in ? new Date(attendance.clock_in).toLocaleTimeString() : "-"}
                    </span>
                  </p>
                  <p className="m-0 text-slate-700 flex justify-between border-b pb-1">
                    <span>Checked out:</span>
                    <span className="font-medium text-slate-900">
                      {attendance.clock_out ? new Date(attendance.clock_out).toLocaleTimeString() : "-"}
                    </span>
                  </p>
                  <p className="m-0 text-slate-700 flex justify-between">
                    <span>Status:</span> 
                    <span className="font-medium capitalize text-slate-900">{attendance.status || "-"}</span>
                  </p>
                </div>
              )}
            </div>

            <div className="rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 p-4 text-white shadow-md">
              <h4 className="font-semibold m-0">Today&apos;s Progress</h4>
              <p className="text-sm text-emerald-100 mb-2">Target achievement</p>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">
                  {totalCompleted} / {totalTarget || 0} units completed
                </span>
                <span className="text-2xl font-bold">{progressPercent}%</span>
              </div>
              <div className="h-2 bg-white/30 rounded-full overflow-hidden">
                <div className="h-full bg-white transition-all duration-500 ease-out" style={{ width: `${Math.min(progressPercent, 100)}%` }} />
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/50">
            <h4 className="font-semibold text-slate-800 m-0">Today&apos;s Work Logs</h4>
            <p className="text-sm text-slate-500 mb-3">{workLogs.length} entries</p>
            {workLogs.length === 0 ? (
              <p className="text-sm text-slate-500 m-0 italic">No work logged for selected date.</p>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                {workLogs.map((log) => (
                  <div
                    key={log.id}
                    className="flex items-center justify-between bg-white border border-slate-100 shadow-sm rounded-lg px-3 py-2"
                  >
                    <div>
                      <p className="m-0 font-medium text-slate-800">
                        {log.item_name || log.reagent_name || log.hardware_name || `Item #${log.reagent_id || log.hardware_id}`}
                      </p>
                      <p className="m-0 text-xs text-slate-500">
                        {log.start_time || "-"} - {log.end_time || "ongoing"}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="m-0 text-lg font-bold text-emerald-600">
                        +{Number(log.quantity_produced || log.quantity_completed || 0)}
                      </p>
                      <p className="m-0 text-xs text-slate-500 font-medium">units</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="pt-2">
            <h4 className="font-semibold text-slate-800 mb-3">Log New Work</h4>
            <form onSubmit={submitMyWork} className="grid md:grid-cols-3 gap-3">
              
              {/* Combined Item Selector */}
              <select
                value={selectedWorkItemValue}
                onChange={handleWorkItemChange}
                className="px-3 py-2 border rounded-lg text-sm"
                required
              >
                <option value="">Select Item (Reagent / Hardware)</option>
                <optgroup label="Reagents">
                  {reagents.map((r) => <option key={`r-${r.id}`} value={`reagent_${r.id}`}>{r.name}</option>)}
                </optgroup>
                <optgroup label="Hardware">
                  {hardwareList.map((h) => <option key={`h-${h.id}`} value={`hardware_${h.id}`}>{h.name}</option>)}
                </optgroup>
              </select>

              <input
                value={workForm.quantity_completed}
                onChange={(e) => setWorkForm({ ...workForm, quantity_completed: e.target.value })}
                type="number"
                min="1"
                placeholder="Quantity"
                className="px-3 py-2 border rounded-lg text-sm"
                required
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  value={workForm.start_time}
                  onChange={(e) => setWorkForm({ ...workForm, start_time: e.target.value })}
                  type="time"
                  className="px-3 py-2 border rounded-lg text-sm text-slate-600"
                  required
                />
                <input
                  value={workForm.end_time}
                  onChange={(e) => setWorkForm({ ...workForm, end_time: e.target.value })}
                  type="time"
                  className="px-3 py-2 border rounded-lg text-sm text-slate-600"
                />
              </div>
              <input
                value={workForm.notes}
                onChange={(e) => setWorkForm({ ...workForm, notes: e.target.value })}
                placeholder="Notes (optional)"
                className="px-3 py-2 border rounded-lg text-sm md:col-span-2"
              />
              <button type="submit" className="px-4 py-2 bg-slate-800 hover:bg-slate-900 transition-colors text-white font-medium rounded-lg text-sm">
                Log My Work
              </button>
            </form>
          </div>
        </div>

        {selectedMember && manageRoster ? (
          <div className="bg-slate-50 border rounded-xl p-4 text-sm text-slate-600">
            Selected: <span className="font-semibold text-slate-800">{selectedMember.name}</span> ({selectedMember.role})
          </div>
        ) : null}
      </div>
    </Wrapper>
  );
}